import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Bot, Send, X, Trash2, Sparkles, Loader2, Maximize2, Minimize2, History, ChevronLeft, MessageSquare, Mic, Square, Volume2, VolumeX, Paperclip, Image as ImageIcon, Zap, Brain, Gauge, Car, Radio } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";
import { toast } from "./ui/sonner";

/* ------------------------------------------------------------------ */
/* Global open helper — anything in the app can call openHidro()       */
/* ------------------------------------------------------------------ */
export function openHidro(opts = {}) {
  // opts: { kickoff?: string } — when present, the assistant auto-sends
  // this as the first user message after opening.
  window.dispatchEvent(new CustomEvent("hidro:open", { detail: opts || {} }));
}

/**
 * Set the live page-level context Hidro should consider on every reply.
 * Any page can call this; Hidro merges it with its base awareness so the
 * conversation feels like one assistant who already knew about your form.
 *
 * @example
 *   setHidroContext({
 *     surface: "recruitment_position_new",
 *     summary: "Owner is drafting a Procurement Manager role.",
 *     form_state: { title: "Procurement Manager", years_min: 3 },
 *     suggestions: ["Help me write the role summary"],
 *   });
 */
export function setHidroContext(detail) {
  window.dispatchEvent(new CustomEvent("hidro:context", { detail: detail || {} }));
}

/**
 * Hook for any creation flow: dispatches `hidro:context` on every change
 * and (optionally) auto-opens Hidro once a "kickoff" condition is met.
 *
 * Usage:
 *   useHidroContext({
 *     surface: "recruitment_position_new",
 *     summary: `Owner is creating a role: "${form.title || "(untitled)"}"`,
 *     formState: form,
 *     suggestions: ["Help me write the role summary", ...],
 *     autoOpenWhen: form.title.trim().length > 3,  // open once title is real
 *     kickoff: "Help me design this role end-to-end.",
 *   });
 */
export function useHidroContext({ surface, summary, formState, suggestions, autoOpenWhen, kickoff }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    setHidroContext({ surface, summary, form_state: formState, suggestions });
  }, [surface, summary, formState, suggestions]);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const openedKey = `hidro_autoopen_${surface}`;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!autoOpenWhen) return;
    if (sessionStorage.getItem(openedKey)) return;
    sessionStorage.setItem(openedKey, "1");
    openHidro({ kickoff });
  }, [autoOpenWhen, openedKey, kickoff]);
}

export function HidroLauncherButton({ className = "", iconClassName = "w-4 h-4", testId = "hidro-launcher-inline" }) {
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  return (
    <button
      type="button"
      onClick={() => openHidro()}
      data-testid={testId}
      aria-label={`Open ${aiName} Assistant`}
      title={`Ask ${aiName}`}
      className={`inline-flex items-center justify-center rounded-md text-[#1FB6C1] hover:text-white hover:bg-[#1FB6C1]/15 ${className}`}
    >
      <Sparkles className={iconClassName} />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Tiny markdown → JSX renderer (only what the assistant actually emits)*/
/* ------------------------------------------------------------------ */
const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
const bareUrlRe = /https?:\/\/[^\s<>"'`)\]]+[^\s<>"'`)\].,;:!?]/g;
const boldRe = /\*\*([^*]+)\*\*/g;
const codeRe = /`([^`]+)`/g;
const italRe = /(?<!\*)\*([^*]+)\*(?!\*)/g;

// A link to this app (any of its hosts) opens in-app; /api/ files open in a new tab.
function inAppPath(href) {
  try {
    const u = new URL(href, window.location.origin);
    const sameApp = u.origin === window.location.origin || /(\.emergent\.host|\.emergentagent\.com)$/.test(u.hostname);
    if (sameApp && !u.pathname.startsWith("/api/")) return `${u.pathname}${u.search}${u.hash}`;
  } catch { /* not a URL */ }
  return null;
}

function mdInline(text) {
  // Order matters: links first (so URL doesn't get italicised) then bold/code/italic
  const out = [];
  let cursor = 0;
  const tokens = [];
  let m;
  // Collect link spans with their indices
  while ((m = linkRe.exec(text)) !== null) {
    tokens.push({ start: m.index, end: m.index + m[0].length, type: "link", label: m[1], href: m[2] });
  }
  while ((m = bareUrlRe.exec(text)) !== null) {
    const s = m.index;
    if (!tokens.some((t) => s >= t.start && s < t.end)) {
      const short = m[0].replace(/^https?:\/\//, "");
      tokens.push({ start: s, end: s + m[0].length, type: "link", label: short.length > 60 ? `${short.slice(0, 57)}…` : short, href: m[0] });
    }
  }
  tokens.sort((a, b) => a.start - b.start);
  for (const t of tokens) {
    if (cursor < t.start) out.push(formatPlain(text.slice(cursor, t.start)));
    const local = t.href.startsWith("/") && !t.href.startsWith("/api/") ? t.href : inAppPath(t.href);
    out.push(
      local ? (
        <Link key={`l-${t.start}`} to={local} data-hidro-link className="text-[#1FB6C1] underline underline-offset-2 hover:text-[#A78BFA]">{t.label}</Link>
      ) : (
        <a key={`l-${t.start}`} href={t.href} target="_blank" rel="noreferrer" className="text-[#1FB6C1] underline underline-offset-2 break-all">{t.label}</a>
      ),
    );
    cursor = t.end;
  }
  if (cursor < text.length) out.push(formatPlain(text.slice(cursor)));
  return out;
}

function formatPlain(s) {
  // Replace bold + code + italic
  const parts = [];
  let rest = s;
  let key = 0;
  const advance = (regex, render) => {
    const next = [];
    for (const piece of (Array.isArray(rest) ? rest : [rest])) {
      if (typeof piece !== "string") { next.push(piece); continue; }
      let last = 0;
      let mm;
      regex.lastIndex = 0;
      while ((mm = regex.exec(piece)) !== null) {
        if (mm.index > last) next.push(piece.slice(last, mm.index));
        next.push(render(mm[1], key++));
        last = mm.index + mm[0].length;
      }
      if (last < piece.length) next.push(piece.slice(last));
    }
    rest = next;
  };
  advance(boldRe, (t, k) => <strong key={`b-${k}`}>{t}</strong>);
  advance(codeRe, (t, k) => <code key={`c-${k}`} className="px-1 py-0.5 rounded bg-white/10 text-[11px] font-mono">{t}</code>);
  advance(italRe, (t, k) => <em key={`i-${k}`}>{t}</em>);
  return parts.concat(rest);
}

function MdBlock({ text }) {
  const lines = (text || "").split(/\r?\n/);
  const blocks = [];
  let buf = [];
  const flush = () => {
    if (!buf.length) return;
    if (buf.every((l) => /^\s*[-•*]\s+/.test(l))) {
      blocks.push(
        <ul key={blocks.length} className="list-disc pl-5 space-y-1 my-1">
          {buf.map((l, i) => (
            <li key={i}>{mdInline(l.replace(/^\s*[-•*]\s+/, ""))}</li>
          ))}
        </ul>,
      );
    } else {
      blocks.push(<p key={blocks.length} className="my-1 leading-relaxed">{mdInline(buf.join(" "))}</p>);
    }
    buf = [];
  };
  for (const ln of lines) {
    if (!ln.trim()) flush();
    else buf.push(ln);
  }
  flush();
  return <div className="text-sm">{blocks}</div>;
}

/* ------------------------------------------------------------------ */
/*  Floating launcher + slide-in panel                                 */
/* ------------------------------------------------------------------ */
const SUGGESTIONS = [
  "How do I create a job card?",
  "Where can I see this month's revenue?",
  "Show me my pending tasks",
  "How do recurring services work?",
  "Where do I invite a customer to the portal?",
];

function suggestionsForPath(path) {
  if (!path) return SUGGESTIONS;
  if (/^\/customers\/[^/]+/.test(path)) {
    return [
      "Summarise this customer in 3 bullets",
      "What's outstanding for this customer?",
      "Schedule a follow-up call",
      "What did we last quote them?",
    ];
  }
  if (/^\/jobcards\/[^/]+/.test(path)) {
    return [
      "Summarise this job card",
      "What's left to complete?",
      "Schedule the follow-up service",
      "Open the linked customer",
    ];
  }
  if (/^\/tickets\/[^/]+/.test(path)) {
    return [
      "Summarise this ticket",
      "Suggest a resolution path",
      "Convert to a job card",
      "Notify the customer",
    ];
  }
  if (/^\/leads\/[^/]+/.test(path)) {
    return [
      "Summarise this lead",
      "What's the next step?",
      "Schedule a callback",
      "Draft a follow-up message",
    ];
  }
  if (path.startsWith("/jobcards")) return ["Which jobs are scheduled today?", "Which jobs slipped past their start date?", "Show me overdue closeouts"];
  if (path.startsWith("/leads")) return ["Which leads went cold this week?", "What's our biggest open lead?", "Show me leads in design/quote phase"];
  if (path.startsWith("/tickets")) return ["Which tickets are urgent?", "Which tickets are unassigned?", "What's our average ticket age?"];
  if (path.startsWith("/customers")) return ["Which customers haven't been contacted this month?", "Which customers had service overdue?"];
  if (path.startsWith("/dashboard") || path === "/") return ["What needs my attention now?", "Run my daily brief", "Show me this week's pipeline"];
  return SUGGESTIONS;
}

function newSessionId() {
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/* Tiny live-updating elapsed-seconds chip for the pending bubble. Helps
   the user feel that Hidro IS working — silence for 20 seconds without a
   counter feels broken; with a counter it just feels slow but alive. */
function PendingElapsed({ startedAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [startedAt]);
  if (!startedAt) return null;
  const sec = Math.max(0, Math.floor((now - startedAt) / 1000));
  return (
    <span
      className="text-[10px] font-mono text-[#647087] tabular-nums"
      data-testid="hidro-pending-elapsed"
    >
      {sec}s
    </span>
  );
}

/* Authenticated image loader for /api/hidro/intakes/{id}/image — the
   endpoint requires a bearer token, so a plain <img src> won't work.
   Fetch via the api client (which injects auth), turn the bytes into a
   blob URL, and revoke it on unmount. Caches per intakeId in module
   scope so re-renders don't re-fetch. */
const _intakeImageCache = new Map();
function IntakeImage({ intakeId, fallbackUrl, alt, className, testId, onClick }) {
  const [src, setSrc] = useState(fallbackUrl || _intakeImageCache.get(intakeId) || "");
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    if (fallbackUrl) { setSrc(fallbackUrl); return; }
    if (!intakeId) return;
    if (_intakeImageCache.has(intakeId)) {
      setSrc(_intakeImageCache.get(intakeId));
      return;
    }
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const resp = await api.get(`/hidro/intakes/${intakeId}/image`, {
          responseType: "blob",
          timeout: 30_000,
        });
        if (cancelled) return;
        createdUrl = URL.createObjectURL(resp.data);
        _intakeImageCache.set(intakeId, createdUrl);
        setSrc(createdUrl);
      } catch (_e) {
        if (!cancelled) setErrored(true);
      }
    })();
    return () => { cancelled = true; };
  }, [intakeId, fallbackUrl]);
  if (errored || !src) return null;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      data-testid={testId}
      onClick={onClick}
      onError={() => setErrored(true)}
    />
  );
}

/* Image-intake proposal action bar — shown under the assistant's reply
   when the message comes from /hidro/intake. Renders the suggested actions
   as explicit buttons so the user must confirm before any prospects are
   created or research jobs queued. Lazily fetches the full intake doc when
   re-rendered from history (where only intakeId is available). */
function IntakeProposalActions({ intakeId, seedIntake, onApplied }) {
  const [intake, setIntake] = useState(seedIntake);
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState({});
  const [err, setErr] = useState("");
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (intake || !intakeId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/hidro/intakes/${intakeId}`);
        if (!cancelled) setIntake(data);
      } catch (_e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [intakeId, intake]);

  if (!intake || hidden) return null;
  const entities = Array.isArray(intake.entities) ? intake.entities : [];
  const businessLike = entities.filter((e) => {
    const k = (e.kind || "").toLowerCase();
    return k === "business" || k === "lodge";
  });
  const recommendedForResearch = entities.filter(
    (e) => (e.recommendation || "").toLowerCase() === "deep_research"
  );
  const alreadyAppliedAction = intake.last_applied_action;
  if (!businessLike.length && !recommendedForResearch.length) return null;

  const apply = async (action, label) => {
    setBusy(action);
    setErr("");
    try {
      const { data } = await api.post(`/hidro/intakes/${intakeId}/apply`, { action });
      setDone((d) => ({ ...d, [action]: true }));
      const summary = action === "create_suspects"
        ? `Added ${data.suspects_total} suspect${data.suspects_total === 1 ? "" : "s"}.`
        : action === "deep_research"
          ? `Queued ${data.research_jobs_total} deep-research job${data.research_jobs_total === 1 ? "" : "s"}. You'll get a notification per result.`
          : `Added ${data.suspects_total} suspect${data.suspects_total === 1 ? "" : "s"} • queued ${data.research_jobs_total} research job${data.research_jobs_total === 1 ? "" : "s"}.`;
      onApplied?.(summary);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mt-2 pt-2 border-t border-white/10 space-y-1.5" data-testid={`hidro-intake-actions-${intakeId}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#647087] flex items-center justify-between">
        <span>Confirm to proceed</span>
        <button
          type="button"
          onClick={() => setHidden(true)}
          className="text-[#647087] hover:text-rose-300"
          title="Dismiss these proposals"
          data-testid={`hidro-intake-dismiss-${intakeId}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {businessLike.length > 0 && (
          <button
            type="button"
            disabled={!!busy || done.create_suspects}
            onClick={() => apply("create_suspects", "Add as Suspects")}
            className="hv-btn hv-btn-ghost text-[11px] py-1"
            data-testid={`hidro-intake-create-suspects-${intakeId}`}
          >
            {busy === "create_suspects" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {done.create_suspects ? "Added" : `Add ${businessLike.length} as Suspect${businessLike.length === 1 ? "" : "s"}`}
          </button>
        )}
        {recommendedForResearch.length > 0 && (
          <button
            type="button"
            disabled={!!busy || done.deep_research}
            onClick={() => apply("deep_research", "Run deep research")}
            className="hv-btn hv-btn-primary text-[11px] py-1"
            data-testid={`hidro-intake-deep-research-${intakeId}`}
          >
            {busy === "deep_research" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {done.deep_research ? "Queued" : `Deep research ${recommendedForResearch.length} prospect${recommendedForResearch.length === 1 ? "" : "s"}`}
          </button>
        )}
        {businessLike.length > 0 && recommendedForResearch.length > 0 && (
          <button
            type="button"
            disabled={!!busy || done.both}
            onClick={() => apply("both", "Add suspects + deep research")}
            className="hv-btn hv-btn-ghost text-[11px] py-1"
            data-testid={`hidro-intake-both-${intakeId}`}
          >
            {busy === "both" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {done.both ? "Done" : "Both"}
          </button>
        )}
      </div>
      {alreadyAppliedAction && Object.keys(done).length === 0 && (
        <div className="text-[10px] text-[#647087]">Last applied: {alreadyAppliedAction}</div>
      )}
      {err && <div className="text-[10px] text-rose-300">{err}</div>}
    </div>
  );
}

export default function HidroAssistant() {
  const { user, hasPermission } = useAuth();
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const appBrand = branding?.app_name || "Rogue";
  const navigate = useNavigate();
  const location = useLocation();
  // Hide the floating bottom-right launcher on fullscreen-chat routes (chat
  // thread detail, engineering-desk session). On those pages the chat
  // composer's own send/record button lives at the bottom-right and the
  // floating launcher would obscure it. The header still has a Hidro
  // launcher so the assistant remains one click away. Locked by the
  // 2026-02 chat-composer overlap bug (Contract #26 follow-on: when a
  // floating control collides with a fixed page control, the floating
  // control yields).
  const isFullscreenChat = (
    /^\/chat\/[^/]+/.test(location.pathname) ||
    /^\/tools\/engineering-desk\/(?!library(?:\/|$))[^/]+/.test(location.pathname)
  );
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessionList, setSessionList] = useState([]);
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("hidro_session") || newSessionId();
  });
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speakOn, setSpeakOn] = useState(() => localStorage.getItem("hidro_speak") === "1");
  // Track which message is currently being spoken-on-demand (shows a Stop icon).
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [chatMode, setChatMode] = useState(() => localStorage.getItem("hidro_chat_mode") || "auto"); // "auto" | "fast" | "deep"
  const [voiceSpeed, setVoiceSpeed] = useState(() => parseFloat(localStorage.getItem("hidro_voice_speed") || "1") || 1);
  const [drivingMode, setDrivingMode] = useState(() => localStorage.getItem("hidro_driving_mode") === "1"); // hands-free, persisted per device
  const [wakeWord, setWakeWord] = useState(() => localStorage.getItem("hidro_wake_word") === "1"); // "Hey Hidro" wake-word listener
  const [wakeListening, setWakeListening] = useState(false); // live status of the SpeechRecognition listener
  const [pendingFile, setPendingFile] = useState(null); // {file, previewUrl}
  // Auto-confirm countdown for proposed actions while in Driving Mode.
  // {seconds, originalReply} — when seconds hits 0 we auto-send "yes".
  const [autoConfirm, setAutoConfirm] = useState(null);
  // Track whether the LAST user message came from the mic — drives the
  // voice_input flag to the backend AND the continuous-loop behaviour.
  const lastInputViaVoiceRef = useRef(false);
  const recRef = useRef(null);
  const recChunksRef = useRef([]);
  const audioRef = useRef(null);
  // Wave 25.16 — TTS performance pack:
  //  ttsCache: in-memory Map<voice::text, blobURL> so re-tapping the same
  //    message replays instantly. LRU-trimmed to last 20 items.
  //  ttsAbortRef: AbortController for the in-flight fetch so a new tap
  //    cancels any prior request that hasn't returned yet.
  const ttsCache = useRef(new Map());
  const ttsAbortRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const sendAbortRef = useRef(null);
  // Wake-word listener (browser SpeechRecognition). Tracked in a ref so we
  // can stop/restart it cleanly without re-renders.
  const wakeRecRef = useRef(null);
  const wakeRestartTimerRef = useRef(null);
  // True while we're in the middle of capturing the COMMAND that follows
  // a wake-word — used to suppress duplicate triggers and to pause the
  // wake listener while Whisper records.
  const wakeArmedRef = useRef(false);
  // Late-bound reference to startRecording — the wake-word useEffect lives
  // ABOVE the const declaration of startRecording (Rules of Hooks: keep
  // hooks before any early return). Setting this ref each render keeps the
  // closure pointing at the latest startRecording without re-triggering
  // the useEffect.
  const startRecordingRef = useRef(null);
  const voiceAllowed = hasPermission ? hasPermission("ai.hidro_voice") : true;
  const wakeWordSupported = typeof window !== "undefined"
    && (window.SpeechRecognition || window.webkitSpeechRecognition);

  // Live page context broadcast by useHidroContext() / setHidroContext().
  // Mirrors what URL-only getPageContext returns but page-broadcast detail
  // wins. Single state object so the chat send call picks it up.
  const [broadcastCtx, setBroadcastCtx] = useState(null);
  const pendingKickoffRef = useRef(null);

  // Listen to the global "open me" event
  useEffect(() => {
    const onOpen = (ev) => {
      setOpen(true);
      // First open after login should land on history
      if (!sessionStorage.getItem("hidro_post_login_seen")) {
        sessionStorage.setItem("hidro_post_login_seen", "1");
        setHistoryOpen(true);
        api.get("/hidro/sessions").then(({ data }) => setSessionList(data || [])).catch(() => {});
      }
      const kickoff = ev?.detail?.kickoff;
      if (kickoff && !pendingKickoffRef.current) {
        pendingKickoffRef.current = kickoff;
      }
    };
    const onCtx = (ev) => {
      const detail = ev?.detail || null;
      setBroadcastCtx(detail);
    };
    window.addEventListener("hidro:open", onOpen);
    window.addEventListener("hidro:context", onCtx);
    return () => {
      window.removeEventListener("hidro:open", onOpen);
      window.removeEventListener("hidro:context", onCtx);
    };
  }, []);

  // After the panel finishes opening and the input is mounted, auto-fire
  // any pending kickoff message exactly once. Driven by ref so it survives
  // re-renders without spamming.
  useEffect(() => {
    if (!open) return;
    if (!pendingKickoffRef.current) return;
    const text = pendingKickoffRef.current;
    pendingKickoffRef.current = null;
    // Tiny delay so the conversation pane is mounted and visible.
    const t = setTimeout(() => { send(text); }, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Persist session id
  useEffect(() => { localStorage.setItem("hidro_session", sessionId); }, [sessionId]);

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (autoConfirm?.timerId) {
      try { clearInterval(autoConfirm.timerId); } catch (_e) { /* noop */ }
    }
  }, [autoConfirm?.timerId]);

  // Persist TTS preference
  useEffect(() => { localStorage.setItem("hidro_speak", speakOn ? "1" : "0"); }, [speakOn]);

  // Persist chat-mode + voice-speed prefs
  useEffect(() => { localStorage.setItem("hidro_chat_mode", chatMode); }, [chatMode]);
  useEffect(() => { localStorage.setItem("hidro_voice_speed", String(voiceSpeed)); }, [voiceSpeed]);
  useEffect(() => { localStorage.setItem("hidro_driving_mode", drivingMode ? "1" : "0"); }, [drivingMode]);
  useEffect(() => { localStorage.setItem("hidro_wake_word", wakeWord ? "1" : "0"); }, [wakeWord]);

  // Driving Mode auto-implies TTS on (you can't be hands-free without audio).
  useEffect(() => { if (drivingMode && !speakOn) setSpeakOn(true); }, [drivingMode]); // eslint-disable-line react-hooks/exhaustive-deps
  // Wake-word ALSO requires Driving Mode + voice perms — auto-disable if either drops.
  useEffect(() => {
    if (wakeWord && (!drivingMode || !voiceAllowed)) setWakeWord(false);
  }, [wakeWord, drivingMode, voiceAllowed]);

  // On open, load history once
  useEffect(() => {
    if (!open || messages.length > 0) return;
    (async () => {
      try {
        const { data } = await api.get(`/hidro/history?session_id=${sessionId}`);
        setMessages((data || []).map((m) => ({
          role: m.role,
          text: m.text,
          intakeId: m.intake_id || null,
          kind: m.kind || null,
        })));
      } catch (_e) { /* ignore — fresh session */ }
    })();
  }, [open, sessionId, messages.length]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  // Auto-focus when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  // Auto-close panel after the user clicks any deep link inside the assistant
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      const a = e.target.closest("[data-hidro-link]");
      if (a) setOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [open]);

  // ─────────────────────── Wake-word listener ───────────────────────
  // Uses the browser's free, on-device SpeechRecognition for ALWAYS-ON
  // wake-phrase detection. When triggered we hand off to the existing
  // MediaRecorder→Whisper flow for the actual command (Whisper has much
  // better accuracy than the in-browser recogniser, and we only pay for
  // the seconds we actually record).
  //
  // MUST live before the early-return at the bottom of this state block
  // so the hook order stays stable (Rules of Hooks).
  // The instance is kept in `wakeRecRef` so we can imperatively stop/start
  // around TTS playback (don't trigger ourselves) and during recording.
  useEffect(() => {
    if (!open || !wakeWord || !drivingMode || !voiceAllowed || !wakeWordSupported) {
      // Tear down any existing instance
      if (wakeRecRef.current) {
        try { wakeRecRef.current.onend = null; } catch (_e) { /* noop */ }
        try { wakeRecRef.current.onerror = null; } catch (_e) { /* noop */ }
        try { wakeRecRef.current.stop(); } catch (_e) { /* noop */ }
        wakeRecRef.current = null;
      }
      if (wakeRestartTimerRef.current) {
        clearTimeout(wakeRestartTimerRef.current);
        wakeRestartTimerRef.current = null;
      }
      setWakeListening(false);
      return undefined;
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    let cancelled = false;

    const fireWake = () => {
      if (wakeArmedRef.current) return;
      wakeArmedRef.current = true;
      // Stop the wake recogniser before we start MediaRecorder — the OS
      // can't share the mic between two consumers cleanly on every browser.
      try { wakeRecRef.current?.stop(); } catch (_e) { /* noop */ }
      // Tiny haptic / audible cue so the user knows it picked up the wake
      // word — short beep via WebAudio. Best-effort, swallow errors.
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = 880; g.gain.value = 0.05;
        o.start(); o.stop(ctx.currentTime + 0.12);
        setTimeout(() => { try { ctx.close(); } catch (_e) { /* noop */ } }, 250);
      } catch (_e) { /* noop */ }
      // Hand off to the existing record→Whisper flow via a ref so this
      // effect doesn't depend on the (later-declared) startRecording closure.
      setTimeout(() => {
        try { startRecordingRef.current?.(); } catch (_e) { /* noop */ }
      }, 220);
    };

    const startInstance = () => {
      if (cancelled) return;
      // Don't compete with TTS playback or an active mic recording.
      if (recording || transcribing || busy || (audioRef.current && !audioRef.current.paused)) {
        wakeRestartTimerRef.current = setTimeout(startInstance, 400);
        return;
      }
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = "en-ZA"; // SAST market — falls back to the closest variant if unavailable
      rec.maxAlternatives = 2;

      rec.onstart = () => { if (!cancelled) setWakeListening(true); };
      rec.onresult = (event) => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const r = event.results[i];
          const transcript = (r[0]?.transcript || "").toLowerCase();
          // Tolerate common ASR mis-spellings: hidro/hydro/heydro
          if (/\b(hey|hi|ok|okay|yo)\s+(hidro|hydro|heydro|heydra|hidra|hidrio|hidrow)\b/.test(transcript)
              || /\b(hidro|hydro)\b\s*[,!.]/.test(transcript)) {
            fireWake();
            break;
          }
        }
      };
      rec.onerror = (e) => {
        if (e?.error === "not-allowed" || e?.error === "service-not-allowed") {
          setError("Microphone permission required for wake-word.");
          setWakeWord(false);
          return;
        }
        // For all other errors, the onend handler will restart us.
      };
      rec.onend = () => {
        if (!cancelled) setWakeListening(false);
        // Re-arm shortly. If the wake-word fired we wait a bit longer so
        // the MediaRecorder pipeline can grab the mic without contention.
        const delay = wakeArmedRef.current ? 1200 : 500;
        wakeRestartTimerRef.current = setTimeout(() => {
          if (!cancelled) {
            wakeArmedRef.current = false;
            startInstance();
          }
        }, delay);
      };
      try {
        rec.start();
        wakeRecRef.current = rec;
      } catch (_e) {
        // start() throws if already started in some browsers — swallow & retry.
        wakeRestartTimerRef.current = setTimeout(startInstance, 600);
      }
    };

    startInstance();

    return () => {
      cancelled = true;
      if (wakeRecRef.current) {
        try { wakeRecRef.current.onend = null; } catch (_e) { /* noop */ }
        try { wakeRecRef.current.stop(); } catch (_e) { /* noop */ }
        wakeRecRef.current = null;
      }
      if (wakeRestartTimerRef.current) {
        clearTimeout(wakeRestartTimerRef.current);
        wakeRestartTimerRef.current = null;
      }
      setWakeListening(false);
    };
  }, [open, wakeWord, drivingMode, voiceAllowed, wakeWordSupported, recording, transcribing, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hide the launcher entirely on the public account-application form
  // (route starts with /apply) and on any portal preview URL — clutter.
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const hideOn = path.startsWith("/apply") || path.startsWith("/portal/preview");
  // Mobile: tapping the "expand" button toggles between full-screen
  // (default — `inset-0`) and a mini bottom-sheet ~55vh.
  // Declared BEFORE the early-return so Hook order stays stable.
  const [mobileMini, setMobileMini] = useState(false);
  if (!user || hideOn) return null;

  const speakReply = async (text, { onEnded, force = false } = {}) => {
    if ((!speakOn && !force) || !text || !voiceAllowed) {
      // Even with TTS off, fire onEnded so callers can drive the loop
      if (typeof onEnded === "function") setTimeout(onEnded, 0);
      return;
    }
    try {
      // Strip markdown for cleaner speech
      const clean = text.replace(/\[[^\]]+\]\([^)]+\)/g, "$1").replace(/[*_`#>]/g, "").slice(0, 1500);
      const voice = localStorage.getItem("hidro_voice") || "alloy";
      // Wave 25.16 — TTS audio cache. Same (text+voice) → same bytes, so
      // a 2nd tap on the same message is INSTANT (no network round-trip).
      // Also de-dupes in-flight requests if the user mashes the button.
      const cacheKey = `${voice}::${clean}`;
      let blobUrl = ttsCache.current.get(cacheKey);
      if (!blobUrl) {
        // Cancel any prior in-flight TTS so we don't wait behind it.
        if (ttsAbortRef.current) {
          try { ttsAbortRef.current.abort(); } catch (_e) { /* noop */ }
        }
        const ctrl = new AbortController();
        ttsAbortRef.current = ctrl;
        const resp = await api.post(
          "/hidro/voice/speak",
          { text: clean, voice },
          { responseType: "blob", signal: ctrl.signal },
        );
        blobUrl = URL.createObjectURL(resp.data);
        // Cap cache to last 20 items so we don't leak memory on long chats.
        if (ttsCache.current.size >= 20) {
          const firstKey = ttsCache.current.keys().next().value;
          const oldUrl = ttsCache.current.get(firstKey);
          ttsCache.current.delete(firstKey);
          try { URL.revokeObjectURL(oldUrl); } catch (_e) { /* noop */ }
        }
        ttsCache.current.set(cacheKey, blobUrl);
      }
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (_e) { /* noop */ }
      }
      const audio = new Audio(blobUrl);
      // Apply user's chosen playback rate (0.75 / 1 / 1.25 / 1.5 / 2)
      try { audio.playbackRate = Math.max(0.5, Math.min(2.5, voiceSpeed || 1)); } catch (_e) { /* noop */ }
      audioRef.current = audio;
      audio.play().catch(() => { /* user gesture required on some browsers */ });
      audio.onended = () => {
        // Don't revoke — keep cached for re-tap. Cache eviction handles it.
        if (typeof onEnded === "function") onEnded();
      };
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        return; // superseded by a newer tap; silent
      }
      // Wave 25.12 — when the user explicitly tapped the speak button
      // (force=true), surface a real error toast so they're not staring at
      // a silent button. Auto-speak (background) stays silent so a flaky
      // TTS doesn't nag on every reply.
      if (force) {
        const msg = e?.response?.data?.detail || e?.message || `${aiName} couldn't read that aloud.`;
        toast.error(typeof msg === "string" ? msg : `${aiName} couldn't read that aloud.`);
      }
      if (typeof onEnded === "function") setTimeout(onEnded, 0);
    }
  };

  // User-initiated "tap to speak" — bypasses the global speakOn toggle and
  // always plays. If the same message is already speaking, stop it.
  const speakMessageOnDemand = async (text, idx) => {
    if (!text) return;
    // Toggle off if already speaking this one
    if (speakingIdx === idx && audioRef.current) {
      try { audioRef.current.pause(); } catch (_e) { /* noop */ }
      setSpeakingIdx(null);
      return;
    }
    // Stop anything else playing first
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_e) { /* noop */ }
    }
    setSpeakingIdx(idx);
    speakReply(text, {
      force: true,
      onEnded: () => setSpeakingIdx(null),
    });
  };

  // Detect current page context so Hidro answers "summarise this" with the
  // right entity. Reads the URL — no extra fetch needed. Pages can also
  // broadcast richer context via `hidro:context` events (form drafts,
  // creation surface, suggestions). Broadcast wins when present.
  const getPageContext = () => {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const url_ctx = (() => {
      const m1 = path.match(/^\/customers\/([^/]+)/);
      if (m1) return { type: "customer", id: m1[1] };
      const m2 = path.match(/^\/jobcards\/([^/]+)/);
      if (m2) return { type: "jobcard", id: m2[1] };
      const m3 = path.match(/^\/tickets\/([^/]+)/);
      if (m3) return { type: "ticket", id: m3[1] };
      const m4 = path.match(/^\/leads\/([^/]+)/);
      if (m4) return { type: "lead", id: m4[1] };
      return null;
    })();
    if (broadcastCtx) {
      return {
        ...(url_ctx || {}),
        broadcast: broadcastCtx,
        path,
      };
    }
    return url_ctx;
  };

  const startRecording = async () => {
    if (recording || transcribing || !voiceAllowed) return;
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) recChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try {
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(recChunksRef.current, { type: rec.mimeType || "audio/webm" });
          if (blob.size < 200) { setError("That was too short — hold the mic for at least a second."); return; }
          setTranscribing(true);
          const fd = new FormData();
          const ext = (rec.mimeType || "").includes("mp4") ? "mp4" : "webm";
          fd.append("audio", blob, `voice.${ext}`);
          const { data } = await api.post("/hidro/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const text = (data?.text || "").trim();
          if (text) {
            lastInputViaVoiceRef.current = true;
            send(text);
          } else {
            setError("Sorry, I couldn't pick that up. Try again?");
          }
        } catch (e) {
          setError(e?.response?.data?.detail || "Voice transcription failed.");
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch (e) {
      setError("Microphone access denied. Allow mic access in your browser.");
    }
  };
  // Sync the late-bound ref (used by the wake-word useEffect, which is
  // defined ABOVE this declaration to comply with Rules of Hooks).
  startRecordingRef.current = startRecording;

  const stopRecording = () => {
    if (!recording) return;
    try { recRef.current?.stop(); } catch (_e) { /* noop */ }
    setRecording(false);
  };

  // Heuristic: does the assistant's reply end in a yes/no confirmation
  // question? Used in Driving Mode to know when to start the 5-second
  // auto-confirm countdown.
  const replyIsAwaitingConfirmation = (text) => {
    if (!text) return false;
    // Strip markdown for matching
    const clean = text.replace(/\[[^\]]+\]\([^)]+\)/g, "$1").replace(/[*_`#>]/g, "");
    const tail = clean.trim().slice(-180).toLowerCase();
    return /(should i (proceed|go ahead|continue|create|book|schedule|log|add|do (it|that))|proceed\??|confirm\??|shall i|do you want me to|say (yes|no)|yes or no)/i.test(tail);
  };

  // Cancel any pending auto-confirm countdown.
  const cancelAutoConfirm = () => {
    if (autoConfirm?.timerId) {
      try { clearInterval(autoConfirm.timerId); } catch (_e) { /* noop */ }
    }
    setAutoConfirm(null);
  };

  // Detect "no/cancel/stop" in a voice transcript so the user can voice-cancel
  // a pending auto-confirm. Returns true if the message was consumed.
  const consumeVoiceCancel = (text) => {
    const t = (text || "").trim().toLowerCase();
    if (!t) return false;
    return /^(no|nope|cancel|stop|abort|don't|dont|negative|never mind|nevermind)\b/.test(t);
  };

  // ─── Wave 27.2 — Streaming chat helper ────────────────────────────
  // POSTs to /api/hidro/chat/stream and reads the SSE response
  // token-by-token, updating the assistant placeholder bubble live.
  // Returns { ok: true, reply, modelUsed } on success.
  // Rejects/throws if the stream errors so send() can fall back to
  // the start+poll path.
  const sendViaStream = async ({ text, ctx, voiceInput, placeholderId, aborter }) => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const tok = (typeof localStorage !== "undefined") ? (localStorage.getItem("token") || "") : "";
    const resp = await fetch(`${base}/api/hidro/chat/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
        ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
      },
      body: JSON.stringify({
        session_id: sessionId,
        message: text,
        context: ctx,
        mode: chatMode,
        autonomous: drivingMode,
        voice_input: voiceInput,
      }),
      signal: aborter.signal,
    });
    if (!resp.ok || !resp.body) {
      throw new Error(`stream ${resp.status}`);
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buf = "";
    let assembledText = "";
    let finalReply = "";
    let modelUsed = "";
    // SSE frames are separated by a blank line ("\n\n"). Each frame
    // has lines like `event: token` and `data: {...json...}`.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let nl;
      while ((nl = buf.indexOf("\n\n")) !== -1) {
        const frame = buf.slice(0, nl);
        buf = buf.slice(nl + 2);
        let evt = "message";
        let dataStr = "";
        for (const line of frame.split("\n")) {
          if (line.startsWith("event:")) evt = line.slice(6).trim();
          else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
        }
        if (!dataStr) continue;
        let parsed;
        try { parsed = JSON.parse(dataStr); } catch { continue; }
        if (evt === "token") {
          assembledText += parsed.delta || "";
          setMessages((m) => m.map((msg) => (
            msg.pendingId === placeholderId
              ? { ...msg, text: assembledText, streaming: true }
              : msg
          )));
        } else if (evt === "intent") {
          // hidro_v2 — surface which specialist took the turn so the
          // user (and dev tools) can see the routing decision. Doesn't
          // replace text, just decorates the bubble.
          setMessages((m) => m.map((msg) => (
            msg.pendingId === placeholderId
              ? { ...msg, specialist: parsed.name }
              : msg
          )));
        } else if (evt === "tool") {
          // Surface a subtle "Looking up X…" hint inside the bubble so
          // the user knows agent work is happening. Doesn't replace
          // streaming — gets overwritten by the next token batch.
          if (parsed.phase === "start" && !assembledText) {
            setMessages((m) => m.map((msg) => (
              msg.pendingId === placeholderId
                ? { ...msg, toolHint: parsed.name }
                : msg
            )));
          }
        } else if (evt === "final") {
          finalReply = (parsed.reply || assembledText || "").trim();
          modelUsed = parsed.model_used || "";
          // [Iter 353 — 2026-02-26] Auto-navigate to the created entity.
          // Dirk: "If you say yes or confirm or any affirmation … then
          // it must create the ticket or job card or meeting or
          // whatever is requested, and then take you to that screen so
          // that you can edit it further." The backend surfaces the
          // created entity's URL as `navigate` on the final frame; we
          // schedule the navigation for after the placeholder replace
          // so the assistant's confirmation bubble finishes rendering.
          if (parsed.navigate && typeof parsed.navigate === "string" && parsed.navigate.startsWith("/")) {
            const target = parsed.navigate;
            setTimeout(() => {
              try {
                navigate(target);
              } catch (e) {
                // If react-router is unavailable in this context (edge
                // case: assistant rendered outside a Router provider),
                // fall back to a hard nav so the promised UX still holds.
                window.location.assign(target);
              }
            }, 400);
          }
        } else if (evt === "error") {
          throw new Error(parsed.error || "stream error");
        }
      }
    }
    // Stream closed. Replace the placeholder with the FINAL message.
    setMessages((m) => m.map((msg) => (
      msg.pendingId === placeholderId
        ? {
            role: "assistant",
            text: finalReply || assembledText || "",
            modelUsed: modelUsed || undefined,
            isEmpty: !(finalReply || assembledText),
            originalQuestion: !(finalReply || assembledText) ? text : undefined,
          }
        : msg
    )));
    return { ok: true, reply: finalReply || assembledText, modelUsed };
  };

  const send = async (textOverride, opts = {}) => {
    const text = (textOverride ?? draft).trim();
    // If a file is attached, route through /hidro/intake (vision pipeline)
    // instead of /hidro/chat. The user message in chat shows both the
    // image preview and their question.
    if (pendingFile) {
      return sendIntake(text);
    }
    if (!text || busy) return;
    // Driving Mode: voice "no/cancel" while a countdown is pending → abort.
    if (autoConfirm && consumeVoiceCancel(text)) {
      cancelAutoConfirm();
      setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "_Cancelled — won't proceed._", kind: "system_note" }]);
      lastInputViaVoiceRef.current = false;
      return;
    }
    // If we had a pending auto-confirm and the user said something else,
    // implicitly cancel the countdown — the user is clearly redirecting.
    if (autoConfirm) cancelAutoConfirm();
    setError("");
    // Capture the voice flag for this turn, then reset for the next one.
    const voiceInput = !!(opts.voiceInput ?? lastInputViaVoiceRef.current);
    lastInputViaVoiceRef.current = false;
    // Optimistic UX: drop the user msg and an *assistant placeholder* in
    // immediately so the user sees a clear typing indicator inside the
    // bubble. Without this, deep-mode (gpt-5) requests look frozen — empty
    // chat for 30-60s with no visible feedback. We replace the placeholder
    // when the real reply lands (or on error / abort).
    const placeholderId = `pending-${Date.now()}`;
    setMessages((m) => [
      ...m,
      { role: "user", text, viaVoice: voiceInput },
      { role: "assistant", text: "", pendingId: placeholderId, mode: chatMode, startedAt: Date.now() },
    ]);
    setDraft("");
    setBusy(true);
    // Pick a generous timeout — deep mode legitimately takes 30-60s. The
    // global axios 25s default is too aggressive for chat reasoning calls.
    const isDeep = chatMode === "deep" || (chatMode === "auto" && (
      text.length > 500 || /\b(research|strategy|playbook|forecast|predict|competitor|deep brief|sales brief|plan a trip|trip plan|build a route|objection|pitch)\b/i.test(text)
    ));
    const ac = new AbortController();
    sendAbortRef.current = ac;
    try {
      const ctx = getPageContext();
      // ─── Wave 27.2 — TRY STREAMING FIRST ─────────────────────────────
      // The /chat/stream endpoint emits SSE token-by-token so the user
      // sees the reply typing live (Cerebrum-on-marketing parity).
      // If streaming fails for any reason (proxy buffering, network,
      // route 404 on stale backends) we fall through to the proven
      // start+poll path below.
      try {
        const streamed = await sendViaStream({
          text,
          ctx,
          voiceInput,
          placeholderId,
          aborter: ac,
        });
        if (streamed?.ok) {
          if (streamed.reply) {
            const awaitingConfirm = drivingMode && replyIsAwaitingConfirmation(streamed.reply);
            speakReply(streamed.reply, {
              onEnded: () => {
                if (!drivingMode) return;
                if (awaitingConfirm) {
                  startAutoConfirmCountdown();
                } else {
                  setTimeout(() => { startRecording().catch(() => {}); }, 350);
                }
              },
            });
          }
          return;
        }
      } catch (streamErr) {
        if (ac.signal.aborted) throw streamErr;
        // Reset the assistant placeholder so the fallback path can
        // re-fill it cleanly — streaming may have appended partial text.
        // eslint-disable-next-line no-console
        console.warn("[Hidro] stream failed, falling back to start+poll:", streamErr?.message || streamErr);
        setMessages((m) => m.map((msg) => (
          msg.pendingId === placeholderId
            ? { ...msg, text: "" }
            : msg
        )));
      }
      // ─── Fallback: start + poll (preserves Cloudflare-edge safety) ──
      // Use the async start+poll pattern instead of a single long-lived
      // request. Long deep-model calls (>100 s) used to surface as
      // Cloudflare 520 errors at the edge even though the OpenAI call
      // succeeded on the backend. Each individual HTTP request here is
      // sub-second, so the proxy stays happy.
      const { data: started } = await api.post(
        "/hidro/chat/start",
        {
          session_id: sessionId,
          message: text,
          context: ctx,
          mode: chatMode,
          autonomous: drivingMode,
          voice_input: voiceInput,
        },
        { timeout: 20000, signal: ac.signal },
      );
      const pendingId = started?.pending_id;
      if (!pendingId) throw new Error(`${aiName} did not return a pending_id`);
      // Poll until status === 'ready' or 'error'. Cap total wait by mode:
      // deep gets 5 min, fast gets 90 s — way more than the model needs
      // but still bounded so a stuck job doesn't hang forever.
      const maxWaitMs = isDeep ? 5 * 60 * 1000 : 90 * 1000;
      const started_at = Date.now();
      let data = null;
      // Adaptive interval — quick first, then back off slightly for deep.
      // Tightened (Wave 25.11) so a fast-mode reply doesn't waste 400ms+ of
      // poll latency on top of the model's own latency. Most fast replies
      // land in 1.5-3s; the first 200ms poll catches them ~0.4s sooner.
      const intervals = isDeep ? [500, 1000, 1800, 2500] : [200, 350, 600, 900, 1400];
      let attempt = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (ac.signal.aborted) throw new DOMException("aborted", "AbortError");
        if (Date.now() - started_at > maxWaitMs) throw new Error(`${aiName} is taking longer than expected.`);
        const wait = intervals[Math.min(attempt, intervals.length - 1)];
        await new Promise((r) => setTimeout(r, wait));
        attempt += 1;
        let resp;
        try {
          resp = await api.get(`/hidro/chat/result/${pendingId}`, { timeout: 15000, signal: ac.signal, validateStatus: (s) => s === 200 || s === 202 });
        } catch (pollErr) {
          // Network blip — keep polling unless aborted.
          if (ac.signal.aborted) throw pollErr;
          continue;
        }
        if (resp.status === 202) continue;
        data = resp.data;
        break;
      }
      if (data?.status === "error") {
        throw new Error(data.error || `${aiName} failed to reply.`);
      }
      const reply = (data?.reply || "").trim();
      // 2026-02-20 (iter 171) — when the backend still emits an empty
      // reply (should be impossible now, but Dirk has seen it in the
      // wild), surface a USEFUL message instead of "tap retry". The
      // server's defense-in-depth catches this on its side too; this
      // is the last-mile cosmetic guard.
      const fallbackEmpty = `_${aiName} produced no visible text this turn. Tap retry, or rephrase with an exact name, ID, or date range._`;
      setMessages((m) => m.map((msg) => (
        msg.pendingId === placeholderId
          ? {
              role: "assistant",
              text: reply || fallbackEmpty,
              modelUsed: data?.model_used,
              isEmpty: !reply,
              originalQuestion: !reply ? text : undefined,
            }
          : msg
      )));
      if (reply) {
        const awaitingConfirm = drivingMode && replyIsAwaitingConfirmation(reply);
        speakReply(reply, {
          onEnded: () => {
            // Driving Mode continuous loop: after the spoken reply ends,
            // either start the auto-confirm countdown OR re-open the mic
            // so the user can speak the next command without touching the screen.
            if (!drivingMode) return;
            if (awaitingConfirm) {
              startAutoConfirmCountdown();
            } else {
              // Tiny delay so the audio-decoder finishes cleanly
              setTimeout(() => { startRecording().catch(() => {}); }, 350);
            }
          },
        });
      }
    } catch (e) {
      const aborted = e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.name === "AbortError" || ac.signal.aborted;
      const isTimeout = e?.code === "ECONNABORTED" || /timeout|taking longer/i.test(e?.message || "");
      const msg = aborted
        ? "Cancelled."
        : isTimeout
          ? `${aiName} is taking longer than usual. Try the Fast mode or rephrase.`
          : (e?.response?.data?.detail || e?.message || `${aiName} is unavailable right now.`);
      setError(typeof msg === "string" ? msg : `${aiName} is unavailable.`);
      setMessages((m) => m.map((x) => (
        x.pendingId === placeholderId
          ? { role: "assistant", text: `_${msg}_`, isEmpty: !aborted, originalQuestion: !aborted ? text : undefined }
          : x
      )));
    } finally {
      sendAbortRef.current = null;
      setBusy(false);
    }
  };

  // Start the 5-second auto-confirm countdown. Each tick updates state so
  // the UI shows the remaining seconds. On expiry we send "yes" automatically.
  // Calling cancelAutoConfirm() (e.g. user taps the cancel button or says "no")
  // clears the timer and aborts the auto-send.
  const startAutoConfirmCountdown = () => {
    cancelAutoConfirm();
    let seconds = 5;
    setAutoConfirm({ seconds, timerId: null });
    const timerId = setInterval(() => {
      seconds -= 1;
      if (seconds <= 0) {
        clearInterval(timerId);
        setAutoConfirm(null);
        // Auto-send "yes" — this triggers the LLM to call its tool.
        send("yes", { voiceInput: true });
      } else {
        setAutoConfirm({ seconds, timerId });
      }
    }, 1000);
    setAutoConfirm({ seconds, timerId });
  };

  const cancelInflight = () => {
    try { sendAbortRef.current?.abort(); } catch (_e) { /* noop */ }
    // Optimistic UX — immediately flip every pending bubble into a
    // "Cancelled." note so the user always sees feedback even when a
    // fast reply was racing the abort.
    setMessages((arr) => arr.map((x) => (
      x.pendingId
        ? { role: "assistant", text: "_Cancelled._" }
        : x
    )));
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(f.type)) {
      setError("Only JPEG, PNG or WEBP images are supported.");
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      setError("Image too large. Max 12 MB.");
      return;
    }
    setError("");
    const previewUrl = URL.createObjectURL(f);
    setPendingFile({ file: f, previewUrl });
  };

  const clearPendingFile = () => {
    if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const sendIntake = async (textOverride) => {
    const prompt = (textOverride ?? draft).trim();
    if (!pendingFile || busy) return;
    setError("");
    const localPreview = pendingFile.previewUrl;
    const file = pendingFile.file;
    // Echo the user's "message" in the chat (image + prompt)
    setMessages((m) => [
      ...m,
      { role: "user", text: prompt || "(Hidro, study this image.)", imageUrl: localPreview, kind: "image_intake" },
    ]);
    setDraft("");
    setPendingFile(null); // detach but DO NOT revoke yet — chat is showing it
    setBusy(true);
    try {
      const ctx = getPageContext();
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prompt", prompt || "Study this image and tell me what to do next.");
      // Driving Mode auto-executes ("act-don't-propose"). In normal mode we
      // PROPOSE — the user confirms via the action buttons in the bubble.
      fd.append("auto_research", drivingMode ? "true" : "false");
      fd.append("auto_create_suspects", drivingMode ? "true" : "false");
      fd.append("session_id", sessionId);
      if (ctx?.type) fd.append("context_type", ctx.type);
      if (ctx?.id) fd.append("context_id", ctx.id);
      const { data } = await api.post("/hidro/intake", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      const queued = data.queued_research || [];
      const created = data.created_suspect_ids || [];
      const tail = [
        created.length ? `\n\n_${created.length} suspect${created.length === 1 ? "" : "s"} added — see [Suspects](/suspects)._` : "",
        queued.length ? `\n\n_${queued.length} deep-research job${queued.length === 1 ? "" : "s"} queued. You'll get a notification per result._` : "",
      ].join("");
      // Backfill the just-pushed user message with the persistent intake_id
      // so when the chat reloads from history the image still renders via
      // /api/hidro/intakes/{id}/image (the local blob URL is volatile).
      setMessages((m) => {
        const copy = [...m];
        for (let i = copy.length - 1; i >= 0; i--) {
          if (copy[i].role === "user" && copy[i].kind === "image_intake" && !copy[i].intakeId) {
            copy[i] = { ...copy[i], intakeId: data.id };
            break;
          }
        }
        copy.push({
          role: "assistant",
          text: (data.reply_markdown || "_Hidro had no reply._") + tail,
          intake: data,
          intakeId: data.id,
          kind: "image_intake_reply",
        });
        return copy;
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || `${aiName} couldn't study that.`;
      setError(typeof msg === "string" ? msg : "Image intake failed.");
      setMessages((m) => [...m, { role: "assistant", text: `_Sorry — I couldn't study that image. ${typeof msg === "string" ? msg : ""}_` }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    try { await api.delete(`/hidro/history?session_id=${sessionId}`); } catch (_e) { /* noop */ }
    const fresh = newSessionId();
    setSessionId(fresh);
    setMessages([]);
    setError("");
  };

  // NOTE: keep these as STATIC class strings so Tailwind JIT extracts them.
  // Concatenating `md:${panelWidth}` produces a class Tailwind never sees,
  // so the expand toggle silently no-ops. This is the canonical bug fix.
  // Wave 25.16 — bumped expanded from 1100×820 → 1600×940.
  // 2026-02 — Dirk reported the expanded panel was wide but short on tall
  // monitors. Now uses `calc(100dvh - 24px)` so it stretches from the top
  // safe margin all the way to the bottom, and we pin `md:!top-3` to undo
  // the `md:top-auto` set on the outer container.
  const panelClasses = expanded
    ? "md:!top-3 md:w-[min(96vw,1600px)] md:h-[calc(100dvh-24px)] md:max-h-[calc(100dvh-24px)]"
    : "md:w-[min(96vw,560px)] md:h-[min(82vh,720px)] md:max-h-[min(82vh,720px)]";
  const mobileLayoutClasses = mobileMini
    ? "inset-x-0 bottom-0 top-auto h-[55vh] !rounded-t-2xl"
    : "inset-0";

  // Detect mobile: when in mobile (<768px) we render the launcher INSIDE
  // the mobile header (see Layout's `<HidroAssistant inline />` slot).
  // The default float-bottom-right launcher is hidden on mobile to avoid
  // covering the bottom nav.
  return (
    <>
      {/* Floating launcher (desktop only) — secondary to the toolbar
          launcher; kept for users who prefer the corner button. Hidden
          on fullscreen-chat routes where it would cover the chat
          composer's send/mic button (2026-02 overlap fix). */}
      {!open && !isFullscreenChat && (
        <button
          type="button"
          onClick={() => openHidro()}
          data-testid="hidro-launcher"
          aria-label={`Open ${aiName} Assistant`}
          className="hidden md:flex fixed z-[1000] bottom-4 right-4 w-9 h-9 rounded-full bg-gradient-to-br from-[#1FB6C1] to-[#A78BFA] text-[#0a0b0e] shadow-[0_0_18px_rgba(31,182,193,0.45)] items-center justify-center hover:scale-105 active:scale-95 transition border border-white/20"
          title={`Ask ${aiName} — your AI guide`}
        >
          <Sparkles className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Slide-in panel — bottom-aligned on desktop, full-screen on mobile */}
      {open && (
        <div
          className={`fixed z-[1000] ${mobileLayoutClasses} md:inset-auto md:bottom-3 md:right-3 md:top-auto md:h-auto md:!rounded-md ${panelClasses} hv-card !p-0 flex flex-col shadow-2xl border-[#1FB6C1]/40 backdrop-blur-xl !rounded-none hv-mobile-app-shell`}
          style={{
            // Mobile: pin the panel to the dynamic viewport — uses 100dvh
            // (via hv-mobile-app-shell CSS class) so the iOS/Android
            // keyboard pushes the composer into view instead of behind
            // the keyboard. Desktop overrides to auto height (set above).
          }}
          data-testid="hidro-panel"
        >
          {/* Header — Wave 27.2 polish: bigger AI dot, calmer typography,
              gradient strip retained. No model badge per Dirk's call. */}
          <div className="flex items-center gap-2 px-3 sm:px-5 py-3 sm:py-4 border-b border-white/10 bg-gradient-to-r from-[#1FB6C1]/[0.12] via-transparent to-[#A78BFA]/[0.12] rounded-t-md hv-safe-top">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1FB6C1] to-[#A78BFA] text-[#0a0b0e] flex items-center justify-center shrink-0 shadow-[0_0_18px_rgba(31,182,193,0.4)]">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-base font-bold text-white leading-tight">
                {aiName}
              </div>
              <div className="text-[11px] text-[#9aa3b5] truncate leading-tight">
                Your in-app guide · scoped to your <span className="text-[#dde3ec]">{user.role}</span> access
              </div>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (!historyOpen) {
                  try {
                    const { data } = await api.get("/hidro/sessions");
                    setSessionList(data || []);
                  } catch (_e) { /* noop */ }
                }
                setHistoryOpen((v) => !v);
              }}
              className="hv-btn hv-btn-ghost min-w-[40px] min-h-[40px] sm:min-w-0 sm:min-h-0 px-2 py-1 text-xs"
              title="Chat history"
              data-testid="hidro-history-btn"
              aria-label="Chat history"
            >
              <History className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => {
                // On mobile (md breakpoint = 768px) toggle the mini bottom-sheet.
                // On desktop toggle the wide panel.
                if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(min-width: 768px)").matches) {
                  setExpanded((e) => !e);
                } else {
                  setMobileMini((m) => !m);
                }
              }}
              className="hv-btn hv-btn-ghost px-2 py-1 text-xs"
              title={
                (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(min-width: 768px)").matches)
                  ? (expanded ? "Collapse" : "Expand")
                  : (mobileMini ? "Full-screen" : "Mini view")
              }
              data-testid="hidro-expand"
            >
              {(expanded || mobileMini) ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              onClick={reset}
              className="hv-btn hv-btn-ghost min-w-[40px] min-h-[40px] sm:min-w-0 sm:min-h-0 px-2 py-1 text-xs"
              title="Start a new chat"
              data-testid="hidro-reset"
              aria-label="New chat"
            >
              <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="hv-btn hv-btn-ghost min-w-[40px] min-h-[40px] sm:min-w-0 sm:min-h-0 px-2 py-1 text-xs"
              title="Close"
              data-testid="hidro-close"
              aria-label="Close"
            >
              <X className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            </button>
          </div>

          {/* History sidebar (overlays messages) */}
          {historyOpen && (
            <div className="absolute inset-x-0 top-[60px] bottom-0 z-10 bg-[#0a0b0e]/95 backdrop-blur-md border-t border-white/10 flex flex-col" data-testid="hidro-history-panel">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
                <button
                  type="button"
                  onClick={() => setHistoryOpen(false)}
                  className="hv-btn hv-btn-ghost px-2 py-1 text-xs"
                  data-testid="hidro-history-back"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Back
                </button>
                <div className="text-xs uppercase tracking-[0.2em] text-[#647087]">Recent chats</div>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {sessionList.length === 0 ? (
                  <div className="text-xs text-[#647087] text-center py-8">No previous conversations yet.</div>
                ) : sessionList.map((s) => (
                  <button
                    key={s.session_id}
                    type="button"
                    onClick={async () => {
                      setSessionId(s.session_id);
                      setMessages([]);
                      setHistoryOpen(false);
                      // Force re-fetch
                      try {
                        const { data } = await api.get(`/hidro/history?session_id=${s.session_id}`);
                        setMessages((data || []).map((m) => ({ role: m.role, text: m.text })));
                      } catch (_e) { /* noop */ }
                    }}
                    className={`w-full text-left p-2.5 rounded border ${s.session_id === sessionId ? "border-[#1FB6C1]/50 bg-[#1FB6C1]/[0.06]" : "border-white/5 hover:bg-white/[0.04]"}`}
                    data-testid={`hidro-history-item-${s.session_id}`}
                  >
                    <div className="text-xs font-medium text-white truncate flex items-center gap-1.5">
                      <MessageSquare className="w-3 h-3 text-[#1FB6C1] shrink-0" />
                      {s.preview || "New conversation"}
                    </div>
                    <div className="text-[10px] font-mono text-[#647087] mt-0.5">
                      {new Date(s.last_at).toLocaleString("en-GB")} · {s.count} msg{s.count === 1 ? "" : "s"}
                    </div>
                  </button>
                ))}
              </div>
              <div className="border-t border-white/10 p-2">
                <button
                  type="button"
                  onClick={() => { reset(); setHistoryOpen(false); }}
                  className="hv-btn hv-btn-ghost text-xs w-full justify-center"
                  data-testid="hidro-history-new"
                >
                  <Sparkles className="w-3 h-3" /> Start a new chat
                </button>
              </div>
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-5 py-4 sm:py-6 space-y-4 overscroll-contain" data-testid="hidro-messages">
            {messages.length === 0 && (
              <div className="space-y-5">
                {/* Wave 27.2 — Cerebrum-style empty state. Bigger
                    typography, breathing room, brand-coloured chips that
                    feel curated instead of dumped into a list. */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1FB6C1] to-[#A78BFA] text-[#0a0b0e] flex items-center justify-center shrink-0 shadow-[0_0_24px_rgba(31,182,193,0.35)]">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-bold text-white">
                      Hi {user.first_name || "there"} — I'm {aiName}.
                    </div>
                    <div className="text-sm text-[#a0aabf] leading-relaxed mt-1">
                      I know your <span className="text-white">{user.role}</span> access in {appBrand}. Ask me anything — navigation, your data, or how to get something done. I'll be useful first.
                    </div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#647087] pt-2">
                  Try asking
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {suggestionsForPath(typeof window !== "undefined" ? window.location.pathname : "").map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => send(s)}
                      className="group text-left text-sm px-3.5 py-3 rounded-xl border border-white/10 bg-white/[0.025] hover:bg-[#1FB6C1]/[0.08] hover:border-[#1FB6C1]/40 transition-all flex items-center justify-between gap-3"
                      data-testid={`hidro-suggestion-${s.slice(0, 12)}`}
                    >
                      <span className="text-[#dde3ec] group-hover:text-white">{s}</span>
                      <Sparkles className="w-3.5 h-3.5 text-[#647087] group-hover:text-[#1FB6C1] shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role !== "user" && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1FB6C1] to-[#A78BFA] text-[#0a0b0e] flex items-center justify-center shrink-0 mt-0.5 shadow-[0_0_12px_rgba(31,182,193,0.25)]">
                    <Sparkles className="w-3.5 h-3.5" />
                  </div>
                )}
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[15px] sm:text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-gradient-to-br from-[#1FB6C1]/20 to-[#1FB6C1]/10 border border-[#1FB6C1]/30 text-white rounded-br-md"
                      : "bg-white/[0.04] border border-white/10 rounded-bl-md"
                  }`}
                  data-hidro-msg-role={m.role}
                >
                  {m.role === "user" && (m.imageUrl || m.intakeId) && (
                    <IntakeImage
                      intakeId={m.intakeId}
                      fallbackUrl={m.imageUrl || null}
                      alt="attached"
                      className="rounded-md max-h-48 mb-2 border border-white/10 object-cover cursor-zoom-in"
                      testId={`hidro-msg-image-${i}`}
                      onClick={(e) => window.open(e.currentTarget.src, "_blank")}
                    />
                  )}
                  {m.pendingId && !m.text ? (
                    <div className="flex items-center gap-2 text-xs text-[#a0aabf]" data-testid={`hidro-msg-pending-${i}`}>
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1FB6C1]" />
                      <span>
                        {m.toolHint
                          ? `Looking up ${String(m.toolHint).replace(/_/g, " ")}…`
                          : (m.mode === "deep" ? "Deep reasoning…" : m.mode === "fast" ? "Thinking…" : "Working on it…")}
                      </span>
                      <PendingElapsed startedAt={m.startedAt} />
                      <button
                        type="button"
                        onClick={cancelInflight}
                        className="ml-1 text-[10px] uppercase tracking-wider text-rose-300/80 hover:text-rose-200"
                        data-testid={`hidro-msg-cancel-${i}`}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <MdBlock text={m.text} />
                      {m.streaming && (
                        <span
                          className="inline-block w-1.5 h-4 align-text-bottom ml-0.5 bg-[#1FB6C1] animate-pulse rounded-sm"
                          aria-hidden="true"
                          data-testid={`hidro-msg-cursor-${i}`}
                        />
                      )}
                    </>
                  )}
                  {m.isEmpty && m.originalQuestion && (
                    <button
                      type="button"
                      onClick={() => {
                        // Replay the question. Drop the empty bubble first.
                        setMessages((arr) => arr.filter((_x, idx) => idx !== i));
                        send(m.originalQuestion);
                      }}
                      className="hv-btn hv-btn-ghost text-[11px] py-1 mt-1.5"
                      data-testid={`hidro-msg-retry-${i}`}
                    >
                      <Loader2 className="w-3 h-3" /> Retry
                    </button>
                  )}
                  {m.role !== "user" && m.kind === "image_intake_reply" && m.intakeId && (
                    <IntakeProposalActions
                      intakeId={m.intakeId}
                      seedIntake={m.intake || null}
                      messageIndex={i}
                      onApplied={(label) => {
                        setMessages((arr) => {
                          const copy = [...arr];
                          copy.push({ role: "assistant", text: `_${label}_`, kind: "system_note" });
                          return copy;
                        });
                      }}
                    />
                  )}
                  {m.modelUsed && m.role !== "user" && (
                    <div className="flex items-center justify-between gap-2 mt-1.5">
                      <div className="text-[9px] text-[#647087] uppercase tracking-wider">
                        {m.modelUsed.includes("mini") ? "Fast" : "Deep"}
                      </div>
                      {m.text && voiceAllowed && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); speakMessageOnDemand(m.text, i); }}
                          className="hv-btn hv-btn-ghost px-1.5 py-0.5 text-[10px]"
                          title={speakingIdx === i ? "Stop" : "Read aloud"}
                          data-testid={`hidro-msg-speak-${i}`}
                          aria-label={speakingIdx === i ? "Stop reading" : "Read aloud"}
                        >
                          {speakingIdx === i
                            ? <Square className="w-3 h-3 text-rose-300" />
                            : <Volume2 className="w-3 h-3 text-[#1FB6C1]" />}
                        </button>
                      )}
                    </div>
                  )}
                  {/* If the assistant message has no modelUsed (e.g. local
                       system_note), still let the user tap-to-speak the body. */}
                  {!m.modelUsed && m.role !== "user" && m.text && !m.pendingId && voiceAllowed && (
                    <div className="flex justify-end mt-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); speakMessageOnDemand(m.text, i); }}
                        className="hv-btn hv-btn-ghost px-1.5 py-0.5 text-[10px]"
                        title={speakingIdx === i ? "Stop" : "Read aloud"}
                        data-testid={`hidro-msg-speak-${i}`}
                      >
                        {speakingIdx === i
                          ? <Square className="w-3 h-3 text-rose-300" />
                          : <Volume2 className="w-3 h-3 text-[#1FB6C1]" />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Inline pending bubble lives in messages now (shown above the
                 composer with a spinner inside the bubble). The old separate
                 "Thinking…" row was removed because users couldn't tell when
                 a deep-mode reply was on its way — they assumed the chat had
                 frozen. See HidroAssistant.send() for the placeholder logic. */}
          </div>

          {/* Composer */}
          <div className="border-t border-white/10 px-3 py-2.5 hv-safe-bottom">
            {error && <div className="text-[10px] text-rose-300 mb-1.5" data-testid="hidro-error">{error}</div>}
            {/* Mode + voice-speed toolbar */}
            <div className="flex items-center justify-between gap-2 mb-1.5 text-[10px]">
              <div className="inline-flex items-center gap-1 bg-white/5 border border-white/10 rounded-md p-0.5" data-testid="hidro-mode-toggle">
                {[
                  { id: "auto", label: "Auto", icon: Sparkles, hint: "Smart-route: simple → fast, complex → deep" },
                  { id: "fast", label: "Fast", icon: Zap, hint: "Snappy replies (best for greetings, lookups)" },
                  { id: "deep", label: "Deep", icon: Brain, hint: "Heavier reasoning (strategy, analysis)" },
                ].map((opt) => {
                  const Icon = opt.icon;
                  const active = chatMode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setChatMode(opt.id)}
                      title={opt.hint}
                      data-testid={`hidro-mode-${opt.id}`}
                      className={`px-1.5 py-0.5 rounded inline-flex items-center gap-1 transition ${
                        active
                          ? "bg-[#1FB6C1]/20 text-[#1FB6C1] border border-[#1FB6C1]/40"
                          : "text-[#9aa3b5] hover:text-white border border-transparent"
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      <span>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
              {voiceAllowed && (
                <button
                  type="button"
                  onClick={() => setDrivingMode((v) => !v)}
                  title={drivingMode
                    ? "Driving Mode is ON — mic auto-restarts after each reply, actions auto-confirm in 5 s"
                    : "Tap to enable Driving Mode (hands-free, continuous voice loop, 5 s auto-confirm)"}
                  data-testid="hidro-driving-toggle"
                  className={`px-1.5 py-0.5 rounded inline-flex items-center gap-1 transition border ${
                    drivingMode
                      ? "bg-[#A78BFA]/20 text-[#A78BFA] border-[#A78BFA]/40 animate-pulse"
                      : "text-[#9aa3b5] hover:text-white border-white/10"
                  }`}
                >
                  <Car className="w-3 h-3" />
                  <span>{drivingMode ? "Driving" : "Drive"}</span>
                </button>
              )}
              {voiceAllowed && drivingMode && (
                <button
                  type="button"
                  onClick={() => {
                    if (!wakeWordSupported) {
                      setError("Wake-word needs Chrome, Edge, or Android Chrome. Use the mic icon instead.");
                      return;
                    }
                    setWakeWord((v) => !v);
                  }}
                  title={!wakeWordSupported
                    ? "Wake-word not supported in this browser. Use Chrome, Edge, or Android Chrome."
                    : wakeWord
                      ? `Wake-word ON ${wakeListening ? "· listening" : "· starting…"} — say "Hey Hidro" anytime`
                      : "Tap to enable wake-word — say \"Hey Hidro\" to start any command"}
                  disabled={!wakeWordSupported}
                  data-testid="hidro-wake-toggle"
                  className={`px-1.5 py-0.5 rounded inline-flex items-center gap-1 transition border ${
                    wakeWord && wakeListening
                      ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/40"
                      : wakeWord
                        ? "bg-amber-500/20 text-amber-300 border-amber-400/40 animate-pulse"
                        : !wakeWordSupported
                          ? "text-[#647087] border-white/5 opacity-50 cursor-not-allowed"
                          : "text-[#9aa3b5] hover:text-white border-white/10"
                  }`}
                >
                  <Radio className={`w-3 h-3 ${wakeWord && wakeListening ? "animate-pulse" : ""}`} />
                  <span>{wakeWord ? (wakeListening ? "Listening" : "Wake") : "Wake"}</span>
                </button>
              )}
              {voiceAllowed && speakOn && (
                <div className="inline-flex items-center gap-1 text-[#9aa3b5]" data-testid="hidro-voice-speed">
                  <Gauge className="w-3 h-3" />
                  <select
                    value={voiceSpeed}
                    onChange={(e) => setVoiceSpeed(parseFloat(e.target.value))}
                    className="bg-transparent border border-white/10 rounded px-1 py-0.5 text-[10px] focus:outline-none focus:border-[#1FB6C1]/40"
                    title="Voice playback speed"
                    data-testid="hidro-voice-speed-select"
                  >
                    <option value={0.75}>0.75×</option>
                    <option value={1}>1×</option>
                    <option value={1.25}>1.25×</option>
                    <option value={1.5}>1.5×</option>
                    <option value={1.75}>1.75×</option>
                    <option value={2}>2×</option>
                  </select>
                </div>
              )}
            </div>
            {recording && (
              <div className="flex items-center gap-2 mb-1.5 text-[11px] text-rose-300" data-testid="hidro-recording-indicator">
                <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" /> Recording — tap stop when done.
              </div>
            )}
            {transcribing && (
              <div className="flex items-center gap-2 mb-1.5 text-[11px] text-[#1FB6C1]" data-testid="hidro-transcribing-indicator">
                <Loader2 className="w-3 h-3 animate-spin" /> Transcribing…
              </div>
            )}
            {pendingFile && (
              <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded border border-[#A78BFA]/40 bg-[#A78BFA]/10" data-testid="hidro-pending-file">
                <img src={pendingFile.previewUrl} alt="attached" className="w-10 h-10 rounded object-cover border border-white/10" />
                <div className="text-[11px] text-[#dde3ec] flex-1 min-w-0 truncate">
                  <span className="text-[#A78BFA] font-bold">Attached:</span> {pendingFile.file.name}
                </div>
                <button
                  type="button"
                  onClick={clearPendingFile}
                  className="text-[#9aa3b5] hover:text-rose-300 shrink-0"
                  title="Remove attachment"
                  data-testid="hidro-pending-file-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {autoConfirm && (
              <div className="flex items-center justify-between gap-2 mb-2 px-2 py-2 rounded border border-[#A78BFA]/40 bg-[#A78BFA]/10" data-testid="hidro-auto-confirm">
                <div className="text-[11px] text-[#dde3ec] flex items-center gap-2">
                  <span className="relative flex w-2.5 h-2.5">
                    <span className="absolute inline-flex w-full h-full rounded-full bg-[#A78BFA] opacity-75 animate-ping" />
                    <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-[#A78BFA]" />
                  </span>
                  <span>
                    <span className="text-[#A78BFA] font-bold">Proceeding in {autoConfirm.seconds}s</span> — say <span className="text-rose-300 font-bold">no</span> or tap cancel.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={cancelAutoConfirm}
                  className="hv-btn hv-btn-ghost text-[10px] py-0.5 px-2"
                  data-testid="hidro-auto-confirm-cancel"
                >
                  Cancel
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onPickFile}
              className="hidden"
              data-testid="hidro-file-input"
            />
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-end gap-2"
            >
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy || recording}
                className="hv-btn hv-btn-ghost px-2 shrink-0"
                title="Attach an image — Hidro will study it"
                data-testid="hidro-attach"
              >
                <Paperclip className="w-3.5 h-3.5" />
              </button>
              <textarea
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={
                  pendingFile
                    ? `What should ${aiName} do with this image? (Optional)`
                    : recording ? "Listening…" : `Ask ${aiName}… (Shift+Enter for newline)`
                }
                rows={1}
                className="hv-input text-sm flex-1 resize-none max-h-32"
                data-testid="hidro-input"
                disabled={busy || recording}
              />
              {voiceAllowed && (
                <>
                  <button
                    type="button"
                    onClick={() => setSpeakOn((v) => !v)}
                    className="hv-btn hv-btn-ghost px-2 shrink-0"
                    title={speakOn ? `${aiName} will speak replies — click to mute` : `Click to let ${aiName} speak replies`}
                    data-testid="hidro-tts-toggle"
                  >
                    {speakOn ? <Volume2 className="w-3.5 h-3.5 text-[#1FB6C1]" /> : <VolumeX className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={recording ? stopRecording : startRecording}
                    disabled={busy || transcribing}
                    className={`hv-btn px-2 shrink-0 ${recording ? "border-rose-400/60 bg-rose-500/15 text-rose-300" : "hv-btn-ghost"}`}
                    title={recording ? "Stop recording" : "Hold to talk — tap mic, speak, tap again to send"}
                    data-testid="hidro-mic"
                  >
                    {recording ? <Square className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                  </button>
                </>
              )}
              <button
                type="submit"
                disabled={busy || (!draft.trim() && !pendingFile)}
                className="hv-btn hv-btn-primary px-3 shrink-0"
                style={{ borderColor: "#1FB6C1", background: "rgba(31,182,193,0.15)", color: "#1FB6C1" }}
                data-testid="hidro-send"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </form>
            <div className="text-[9px] text-[#647087] mt-1.5 text-center">
              Hidro can make mistakes — double-check critical actions.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
